import { _js } from '@ifixit/utils/localize';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { toast } from '@ifixit/ui/components/toast';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@ifixit/ui/components/alert-dialog';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@ifixit/ui/components/sheet';
import { cn } from '@ifixit/ui/lib/utils';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WriteStatusAnnouncer } from 'Shared/editor/action_bar';
import { PrimaryButton } from 'Shared/editor/buttons';
import { SectionCard } from 'Shared/editor/section_card';
import { StepEditor } from './StepEditor';
import { seedFromStep, type StepSeed } from './step_seed';
import type { ApiGuide, ApiStep, GuideVideoInfo } from './types';
import { movedItem, useDragReorder } from 'Shared/editor/use_drag_reorder';
import { useStepOrder } from './use_guide_edit_api';

type Selection = number | 'new' | null;

/**
 * The step selection encoded in the URL hash — #steps/123 or #steps/new —
 * so a selected step survives refresh and back/forward walks the steps
 * the user visited. Null when the hash names no step (another tab, or a
 * bare #steps).
 */
function selectionFromHash(): Selection {
   const [tab, step] = window.location.hash.slice(1).split('/');
   if (tab !== 'steps' || !step) {
      return null;
   }
   if (step === 'new') {
      return 'new';
   }
   const stepid = Number(step);
   return Number.isSafeInteger(stepid) && stepid > 0 ? stepid : null;
}

/**
 * Point the hash at a selection. Assigning location.hash pushes a history
 * entry (that's what makes Back work); `replace` rewrites the current
 * entry instead, for programmatic corrections — a created step replacing
 * #steps/new, a deletion, normalizing a bare #steps — that shouldn't
 * become Back stops.
 */
function writeSelectionHash(selection: Selection, options?: { replace: boolean }) {
   if (selection === null) {
      return;
   }
   const target = selection === 'new' ? 'steps/new' : `steps/${selection}`;
   if (window.location.hash.slice(1) === target) {
      return;
   }
   if (options?.replace) {
      const url = new URL(window.location.href);
      url.hash = target;
      window.history.replaceState(window.history.state, '', url);
      // replaceState doesn't fire hashchange; the tab hook reads the hash
      // through it, so poke it to re-read.
      window.dispatchEvent(new HashChangeEvent('hashchange'));
   } else {
      window.location.hash = target;
   }
}

type Drag = ReturnType<typeof useDragReorder>;

// The step editor hosts Tiptap toolbars and the whole line list; without
// memo every rail interaction (drag preview, selection ring) re-rendered it.
const MemoStepEditor = memo(StepEditor);

const noopCreated = () => {};

function stepThumb(step: ApiStep): string | null {
   if (step.media.type !== 'image' || step.media.data.length === 0) {
      return null;
   }
   const image = step.media.data[0];
   return image.thumbnail ?? image.standard ?? image.original ?? null;
}

/**
 * The large rendition the editor's preview shows for a step's primary image
 * (mirrors StepMediaSelector's previewUrl precedence), or null when the step
 * has no image. This is what a step switch has to download.
 */
function stepPreviewUrl(step: ApiStep): string | null {
   if (step.media.type !== 'image' || step.media.data.length === 0) {
      return null;
   }
   const image = step.media.data[0];
   return image.large ?? image.medium ?? image.standard ?? image.original ?? null;
}

/** How many steps on each side of the selected one to warm. */
const IMAGE_PREFETCH_RADIUS = 2;

/**
 * Warm the browser image cache for the steps on either side of the selected
 * one, so clicking through the rail shows the next step's preview without a
 * download wait. The rendition URLs already came down with the guide GET, so
 * this only primes the HTTP cache — no API calls. Windowed to ±radius so a
 * 100-step guide doesn't pull every large rendition (the rail already loads
 * the small thumbnails), and run at idle so it never competes with the
 * current step's own render.
 */
function usePrefetchNeighborImages(steps: ApiStep[], selectedIndex: number) {
   const warmedRef = useRef<Set<string>>(new Set());
   useEffect(() => {
      if (selectedIndex < 0) {
         return;
      }
      const urls: string[] = [];
      for (let offset = -IMAGE_PREFETCH_RADIUS; offset <= IMAGE_PREFETCH_RADIUS; offset++) {
         if (offset === 0) {
            continue; // The selected step loads through its own editor.
         }
         const neighbor = steps[selectedIndex + offset];
         const url = neighbor ? stepPreviewUrl(neighbor) : null;
         if (url !== null && !warmedRef.current.has(url)) {
            urls.push(url);
         }
      }
      if (urls.length === 0) {
         return;
      }
      const warm = () => {
         for (const url of urls) {
            warmedRef.current.add(url);
            new Image().src = url;
         }
      };
      if (typeof window.requestIdleCallback === 'function') {
         const handle = window.requestIdleCallback(warm);
         return () => window.cancelIdleCallback(handle);
      }
      const handle = window.setTimeout(warm, 200);
      return () => window.clearTimeout(handle);
   }, [steps, selectedIndex]);
}

/**
 * The Steps tab: an orderable step rail and the editor for the selected
 * step. Steps save individually (each has its own revisionid), so switching
 * steps warns when the current one has unsaved changes.
 *
 * memo: every keystroke in the Introduction/Details forms re-renders the
 * page, and forceMount keeps this tab mounted; its props only change when
 * the guide itself does.
 */
export const StepsTab = memo(function StepsTab({
   guideid,
   guide,
   initialStepid,
   initialNewStepOrderby,
   video,
   onDirtyChange,
   onOpenShortcuts,
}: {
   guideid: number;
   guide: ApiGuide;
   initialStepid: number | null;
   /** Insert position from a new-after step URL; null everywhere else. */
   initialNewStepOrderby: number | null;
   video: GuideVideoInfo | null;
   onDirtyChange: (dirty: boolean) => void;
   /** Opens the page-level keyboard-shortcuts dialog (Shift+/). */
   onOpenShortcuts: () => void;
}) {
   const [selected, setSelected] = useState<Selection>(() => {
      // The hash wins over the server-parsed URL: it's what a refresh (or
      // a reopened tab) carries after in-page navigation.
      const fromHash = selectionFromHash();
      if (fromHash === 'new') {
         return 'new';
      }
      if (typeof fromHash === 'number' && guide.steps.some(step => step.stepid === fromHash)) {
         return fromHash;
      }
      if (initialNewStepOrderby !== null) {
         return 'new';
      }
      if (initialStepid && guide.steps.some(step => step.stepid === initialStepid)) {
         return initialStepid;
      }
      return guide.steps[0]?.stepid ?? 'new';
   });
   // Where a created step lands: the new-after URL's position on first use,
   // append-at-end afterwards. Clamped to a valid insert position.
   const [newStepOrderby, setNewStepOrderby] = useState<number | null>(
      initialNewStepOrderby === null
         ? null
         : Math.min(Math.max(initialNewStepOrderby, 1), guide.steps.length + 1)
   );
   const stepOrder = useStepOrder(guideid);
   const stepDirtyRef = useRef(false);
   // A step the user tried to switch to while the current one had unsaved
   // changes; non-null renders the discard confirmation dialog.
   const [pendingSelection, setPendingSelection] = useState<Selection>(null);
   // The mobile "Rearrange steps" disclosure; closed on load so the editor
   // leads, and closed again once a step is picked from it.
   const [railOpen, setRailOpen] = useState(false);
   // Live editor state carried from a just-created step so edits made while
   // the create POST was in flight survive the remount onto the new stepid.
   const [createdSeed, setCreatedSeed] = useState<{ stepid: number; seed: StepSeed } | null>(null);
   // A duplicate's prefill for the 'new' editor. The nonce keys the editor
   // so duplicating remounts it onto the new seed even when the selection
   // is already 'new'.
   const [newStepSeed, setNewStepSeed] = useState<{ seed: StepSeed; nonce: number } | null>(null);
   const seedNonceRef = useRef(0);
   // What the next switch to the 'new' editor opens with (insert position
   // and optional duplicate prefill). Held in a ref and consumed only when
   // the selection actually lands on 'new' (through selectStep or the
   // discard dialog), so a rejected switch never clobbers a dirty editor.
   const stagedNewRef = useRef<{ orderby: number | null; seed: StepSeed | null }>({
      orderby: null,
      seed: null,
   });
   // Gates the page-level shortcuts to when this tab is actually shown
   // (forceMount keeps it in the DOM behind the other tabs).
   const rootRef = useRef<HTMLDivElement>(null);
   // The rail's own scroll container (md+): long guides scroll the step
   // list inside a viewport-height rail instead of stretching the page.
   const railScrollRef = useRef<HTMLDivElement>(null);
   // The sticky wrapper whose height is measured to fit the viewport.
   const railWrapRef = useRef<HTMLDivElement>(null);

   // Size the rail to reach exactly the viewport bottom, whether the page
   // sits at the top (the rail starts below the page chrome) or is scrolled
   // (sticky pins it 16px from the top). CSS sticky can't express "from
   // wherever my top currently is to the viewport bottom", and a fixed
   // 100vh cap overflowed the page with its own scrollbar at load. This is
   // a fixed height, not a cap: the editor card matches the rail, so a
   // short step list must not shrink the editing surface — both panels
   // always fill the viewport. Writes the style directly — a React state
   // here would re-render the whole tab on every scroll frame.
   useEffect(() => {
      const wrap = railWrapRef.current;
      if (!wrap) {
         return;
      }
      let frame = 0;
      const update = () => {
         frame = 0;
         // Hidden behind another tab (forceMount): rects are zero and any
         // measurement is garbage. The ResizeObserver below re-fires when
         // the tab becomes visible.
         if (wrap.offsetParent === null) {
            return;
         }
         const top = Math.max(16, wrap.getBoundingClientRect().top);
         const max = window.innerHeight - top - 16;
         wrap.style.height = `${max}px`;
         // The page's trailing padding still leaves a sliver of scrollbar
         // when the rail is the tallest thing on it; shave exactly that
         // off. A large overflow means the editor column (or a scrolled
         // page) is driving the height — leave the rail alone then.
         const overflow =
            document.documentElement.scrollHeight - document.documentElement.clientHeight;
         if (window.scrollY === 0 && overflow > 0 && overflow <= 64) {
            wrap.style.height = `${max - overflow}px`;
         }
      };
      const schedule = () => {
         frame ||= requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      // Catches what the window listeners can't: the tab becoming visible
      // and layout shifts above the rail changing its top edge.
      const observer = new ResizeObserver(schedule);
      observer.observe(wrap);
      return () => {
         cancelAnimationFrame(frame);
         window.removeEventListener('scroll', schedule);
         window.removeEventListener('resize', schedule);
         observer.disconnect();
      };
   }, []);

   // Keep the selection visible in the scrolling rail — a deep link lands
   // on it, and the Shift+;/H step walk can otherwise wander offscreen.
   // 'new' scrolls to the draft row so an insert position from a new-after
   // URL is on screen.
   useEffect(() => {
      if (typeof selected !== 'number' && selected !== 'new') {
         return;
      }
      railScrollRef.current
         ?.querySelector(`[data-railstep="${selected}"]`)
         ?.scrollIntoView({ block: 'nearest' });
   }, [selected]);

   // Reordering stages locally and applies in one PUT when "Save step
   // order" is clicked — live-saving every move was slow, and on phones a
   // mid-drag save felt like the list glitching.
   const [pendingOrder, setPendingOrder] = useState<number[] | null>(null);

   // The rail's display order: the staged order reconciled against the
   // server's current steps (a step added or deleted while an order is
   // staged drops out of / appends to the staging instead of breaking it).
   const orderedSteps = useMemo(() => {
      if (pendingOrder === null) {
         return guide.steps;
      }
      const byId = new Map(guide.steps.map(step => [step.stepid, step]));
      const staged = pendingOrder.flatMap(stepid => byId.get(stepid) ?? []);
      const stagedIds = new Set(pendingOrder);
      return [...staged, ...guide.steps.filter(step => !stagedIds.has(step.stepid))];
   }, [guide.steps, pendingOrder]);

   const orderedIds = orderedSteps.map(step => step.stepid);
   const orderDirty =
      pendingOrder !== null &&
      orderedIds.join(',') !== guide.steps.map(step => step.stepid).join(',');
   // Render-time reset (the sanctioned derived-state adjustment): once the
   // server catches up with the staged order — the refetch after a save,
   // or the user moving things back — the staging is spent.
   if (pendingOrder !== null && !orderDirty) {
      setPendingOrder(null);
   }

   // While the new-step editor is open, the unsaved step rides in the rail
   // as a draft row at its insert position: it takes a real step number
   // (the rows below shift by one) and drags like any other row. Clamped:
   // a deletion can shrink the list under a staged position.
   const draftIndex =
      selected === 'new'
         ? Math.min((newStepOrderby ?? orderedSteps.length + 1) - 1, orderedSteps.length)
         : null;
   const railEntries: (ApiStep | 'draft')[] = useMemo(() => {
      if (draftIndex === null) {
         return orderedSteps;
      }
      const entries: (ApiStep | 'draft')[] = [...orderedSteps];
      entries.splice(draftIndex, 0, 'draft');
      return entries;
   }, [orderedSteps, draftIndex]);

   // Refs mirror the values the stable callbacks below read, so the
   // memoized rail rows and step editor don't re-render just because a
   // callback identity changed.
   const selectedRef = useRef(selected);
   const orderDirtyRef = useRef(orderDirty);
   const onDirtyChangeRef = useRef(onDirtyChange);
   const newStepOrderbyRef = useRef(newStepOrderby);
   const orderedStepsRef = useRef(orderedSteps);
   // An unsaved step order counts as unsaved changes too (it arms the
   // page's leave guard); staging changes without a step edit still needs
   // to report, hence the every-render sync.
   useEffect(() => {
      selectedRef.current = selected;
      orderDirtyRef.current = orderDirty;
      onDirtyChangeRef.current = onDirtyChange;
      newStepOrderbyRef.current = newStepOrderby;
      orderedStepsRef.current = orderedSteps;
      onDirtyChange(stepDirtyRef.current || orderDirty);
   });

   const setStepDirty = useCallback((dirty: boolean) => {
      stepDirtyRef.current = dirty;
      onDirtyChangeRef.current(dirty || orderDirtyRef.current);
   }, []);

   /**
    * Consume the staged insert position and duplicate seed into state, as
    * the selection lands on 'new'. Consuming (not just reading) means a
    * later plain switch to 'new' — the footer button, the hash — opens the
    * usual empty append editor instead of replaying a stale intent.
    */
   const applyStagedNew = useCallback(() => {
      const staged = stagedNewRef.current;
      stagedNewRef.current = { orderby: null, seed: null };
      // No upper clamp needed: the draft-row math caps the rail position at
      // the end of the list when steps were deleted underneath the intent.
      setNewStepOrderby(staged.orderby);
      seedNonceRef.current += 1;
      setNewStepSeed(staged.seed ? { seed: staged.seed, nonce: seedNonceRef.current } : null);
   }, []);

   const selectStep = useCallback(
      (target: Selection) => {
         if (target === selectedRef.current) {
            return;
         }
         if (stepDirtyRef.current) {
            setPendingSelection(target);
            return;
         }
         setStepDirty(false);
         setSelected(target);
         if (target === 'new') {
            applyStagedNew();
         }
         // A history entry per visited step, so Back retraces them. When
         // the selection came FROM the hash (back/forward), this no-ops.
         writeSelectionHash(target);
         setCreatedSeed(null);
         setRailOpen(false);
      },
      [applyStagedNew, setStepDirty]
   );

   /**
    * Route to the 'new' editor with an insert position and (for duplicates)
    * a prefill, through the same dirty guard selectStep applies. Covers the
    * one gap selectStep can't: restaging while 'new' is already selected,
    * where the seed nonce remounts the editor onto the new intent.
    */
   const stageNewStep = useCallback(
      (orderby: number | null, seed: StepSeed | null) => {
         stagedNewRef.current = { orderby, seed };
         if (selectedRef.current === 'new') {
            if (stepDirtyRef.current) {
               setPendingSelection('new');
               return;
            }
            applyStagedNew();
            setRailOpen(false);
            return;
         }
         selectStep('new');
      },
      [applyStagedNew, selectStep]
   );

   const insertStepRelative = useCallback(
      (stepid: number, where: 'above' | 'below') => {
         const index = orderedStepsRef.current.findIndex(step => step.stepid === stepid);
         if (index === -1) {
            return;
         }
         // Positions are relative to the saved steps (not the displayed
         // rows), so an already-open draft row can't shift the target.
         stageNewStep(index + (where === 'above' ? 1 : 2), null);
      },
      [stageNewStep]
   );

   const duplicateStep = useCallback(
      (stepid: number) => {
         const steps = orderedStepsRef.current;
         const index = steps.findIndex(step => step.stepid === stepid);
         if (index === -1) {
            return;
         }
         // Just below the source; the author reviews and creates the copy.
         stageNewStep(index + 2, seedFromStep(steps[index]));
      },
      [stageNewStep]
   );

   // Distinguishes the discard close from a "Keep editing" close in
   // onOpenChange, which runs after this with stale state either way.
   const discardingRef = useRef(false);

   const discardAndSelect = () => {
      discardingRef.current = true;
      setStepDirty(false);
      setSelected(pendingSelection);
      if (pendingSelection === 'new') {
         applyStagedNew();
      }
      writeSelectionHash(pendingSelection);
      setPendingSelection(null);
      setCreatedSeed(null);
      setRailOpen(false);
   };

   // Programmatic selection changes (a create replacing #steps/new, a
   // delete, a refetch dropping the selected step) rewrite the current
   // entry — they correct a URL that now names a dead state, so they must
   // not become Back stops. User navigation already wrote the hash and
   // no-ops here. Guarded to the steps tab: this tab stays mounted (and
   // its selection state live) while the hash points at another tab.
   //
   // Declared BEFORE the hash-apply effect below: a create commits the new
   // guide.steps and the selection change together, re-running that effect,
   // and it must read the corrected hash — reading the stale #steps/new
   // yanked the selection back to an empty new-step editor.
   useEffect(() => {
      if (window.location.hash.slice(1).split('/')[0] === 'steps') {
         writeSelectionHash(selected, { replace: true });
      }
   }, [selected]);

   // Back/forward (and hand-edited URLs): apply the hash's step through
   // selectStep so the dirty-step guard still gets its say. A bare #steps
   // (the tab strip writes that) is normalized to the current selection so
   // the URL always names the step on screen.
   useEffect(() => {
      const applyHash = () => {
         const target = selectionFromHash();
         if (target === null) {
            if (window.location.hash.slice(1) === 'steps') {
               writeSelectionHash(selectedRef.current, { replace: true });
            }
            return;
         }
         if (typeof target === 'number' && guide.steps.every(step => step.stepid !== target)) {
            return;
         }
         selectStep(target);
      };
      applyHash();
      window.addEventListener('hashchange', applyHash);
      return () => window.removeEventListener('hashchange', applyHash);
   }, [guide.steps, selectStep]);

   // Rail reordering is drag and drop only (mouse and touch). A commit
   // reconstructs the previewed order of the combined list, then splits
   // it: the draft row's slot updates the create position, the saved
   // steps' order stages as a pending reorder (a draft-only move stages
   // the unchanged order, which the render-time reset above clears).
   const drag = useDragReorder((from, to) => {
      const next = movedItem(railEntries, from, to);
      const draftAt = next.indexOf('draft');
      if (draftAt !== -1) {
         setNewStepOrderby(draftAt + 1);
      }
      setPendingOrder(next.flatMap(entry => (entry === 'draft' ? [] : [entry.stepid])));
   });

   const saveOrder = () => {
      stepOrder.mutate(
         { revisionid: guide.revisionid, stepids: orderedIds },
         {
            // The button disappearing is the only other signal; make the
            // outcome legible mid-batch like the guide-level Save toast.
            onSuccess: () => {
               toast.success(_js('Step order saved.'), { duration: 4000 });
            },
            onError: () => {
               toast.error(
                  _js('The step order did not save — the guide changed underneath. Try again.'),
                  { duration: 5000 }
               );
            },
         }
      );
   };

   const selectedStep =
      typeof selected === 'number'
         ? (guide.steps.find(step => step.stepid === selected) ?? null)
         : null;

   // The selected step vanished from a refetch (another session deleted it)
   // while steps remain: reconcile to the first step so the editor pane
   // doesn't fall through to the "no steps yet" empty state.
   if (typeof selected === 'number' && selectedStep === null && guide.steps.length > 0) {
      setSelected(guide.steps[0].stepid);
   }

   // Prime the neighboring steps' preview images so rail click-through is
   // instant. -1 (a 'new'/null selection) warms nothing.
   const selectedIndex =
      typeof selected === 'number' ? guide.steps.findIndex(step => step.stepid === selected) : -1;
   usePrefetchNeighborImages(guide.steps, selectedIndex);

   // Step-level shortcuts: ↑/↓ walk the steps, Shift+/ opens the help,
   // Ctrl+Shift+Enter adds a step. Shift+; and Shift+H are undocumented
   // legacy synonyms for the arrows. Plain characters and bare arrows only
   // apply outside text inputs and dialogs; the line-level Ctrl shortcuts
   // live in the step editor.
   useEffect(() => {
      const onKeyDown = (event: KeyboardEvent) => {
         // The step rail's grips consume arrow keys to reorder steps.
         if (event.defaultPrevented) {
            return;
         }
         // Add a step, even while a line editor has focus (the line editor
         // leaves Ctrl+Shift+Enter unconsumed so it bubbles here). Gated to
         // the Steps tab being on screen; selectStep no-ops if already on
         // the new-step row.
         if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'Enter') {
            if (rootRef.current && rootRef.current.offsetParent !== null) {
               selectStep('new');
               event.preventDefault();
            }
            return;
         }
         if (event.ctrlKey || event.metaKey || event.altKey) {
            return;
         }
         const isArrow = !event.shiftKey && (event.key === 'ArrowDown' || event.key === 'ArrowUp');
         if (!isArrow && !event.shiftKey) {
            return;
         }
         const target = event.target;
         if (
            target instanceof Element &&
            target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')
         ) {
            return;
         }
         if (!rootRef.current || rootRef.current.offsetParent === null) {
            return;
         }
         if (event.key === '?') {
            onOpenShortcuts();
            event.preventDefault();
            return;
         }
         const isNext = (isArrow && event.key === 'ArrowDown') || event.key === ':';
         const isPrevious =
            (isArrow && event.key === 'ArrowUp') ||
            (event.shiftKey && event.key.toLowerCase() === 'h');
         if (!isNext && !isPrevious) {
            return;
         }
         // Walk the displayed order (which includes a staged, unsaved
         // reorder), not the server order.
         const index = orderedSteps.findIndex(step => step.stepid === selected);
         if (index === -1) {
            return;
         }
         const neighbor = orderedSteps[isNext ? index + 1 : index - 1];
         if (neighbor) {
            selectStep(neighbor.stepid);
         }
         event.preventDefault();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   });

   const handleDeleted = useCallback(() => {
      setStepDirty(false);
      const remaining = guide.steps.find(step => step.stepid !== selectedRef.current);
      setSelected(remaining?.stepid ?? 'new');
   }, [guide.steps, setStepDirty]);

   const handleCreated = useCallback(
      (stepid: number, seed?: StepSeed) => {
         setStepDirty(false);
         // A staged order predates the new stepid; without this splice the
         // reconciliation appends the created step at the end of the staged
         // list, teleporting it away from where the draft row sat.
         const at = newStepOrderbyRef.current;
         setPendingOrder(prev => {
            if (prev === null || prev.includes(stepid)) {
               return prev;
            }
            const next = [...prev];
            next.splice(Math.min((at ?? next.length + 1) - 1, next.length), 0, stepid);
            return next;
         });
         setNewStepOrderby(null);
         setNewStepSeed(null);
         stagedNewRef.current = { orderby: null, seed: null };
         setCreatedSeed(seed ? { stepid, seed } : null);
         setSelected(stepid);
      },
      [setStepDirty]
   );

   // Allocating 100+ option elements on every render added up; the list
   // only changes with the (staged) step order or the draft row. The draft
   // takes its number here too, so the phone picker agrees with the rail.
   // The same rail renders in the md+ sidebar column and inside the phone
   // sheet; the sheet's copy closes the sheet after acting.
   // Add a step keeps the full row until an unsaved order needs a home;
   // then the two share it. Kept outside the rail's scroll area so a long
   // guide never buries the buttons.
   return (
      <div
         ref={rootRef}
         // Phones stack the step picker over the editor; tighten that gap so
         // the first image sits higher in the viewport. md+ keeps gap-4 as
         // the rail/editor column gutter.
         className="grid grid-cols-1 gap-2 md:grid-cols-[280px_1fr] md:gap-4"
      >
         {/* At the root rather than beside the Save step order button: the
             rail renders twice, and the phone copy unmounts with its sheet —
             a live region has to already be in the tree when the write
             starts to announce it. */}
         <WriteStatusAnnouncer isSaving={stepOrder.isPending} />
         {/* The rail renders twice: a plain column on md+ and a "Rearrange
             steps" disclosure below the editor on phones. CSS swaps them so
             the first paint is right without waiting on a media query.
             On md+ the rail pins to the viewport and the step list scrolls
             inside itself — a long guide would otherwise stretch the page
             to the full rail height. */}
         <div
            ref={railWrapRef}
            // A bordered card, like the editor's other containers: the list
            // scrolls inside it and the Add-a-step footer stays pinned below
            // a divider, so a long list visibly clips at the card's edge
            // instead of cutting rows off mid-air. overflow-hidden caps the
            // wrapper to its max height: without it the inner scroll
            // container still lays out its full content height into the
            // page. The card's own padding is what gives the rows' borders
            // room, replacing the old -mx-1/px-1 clip tricks.
            className="hidden rounded-2xl border border-gray-300 bg-white md:sticky md:top-4 md:flex md:max-h-[calc(100vh-2rem)] md:flex-col md:self-start md:overflow-hidden"
         >
            <div
               ref={railScrollRef}
               className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 scrollbar-minimal"
            >
               <RailList
                  drag={drag}
                  entries={railEntries}
                  selected={selected}
                  hasVideo={video !== null}
                  onSelect={selectStep}
                  onInsert={insertStepRelative}
                  onDuplicate={duplicateStep}
               />
            </div>
            <div className="border-t border-gray-200 p-3">
               <RailFooter
                  addDisabled={selected === 'new'}
                  orderDirty={orderDirty}
                  orderSaving={stepOrder.isPending}
                  onAdd={() => stageNewStep(null, null)}
                  onSaveOrder={saveOrder}
               />
            </div>
         </div>

         {/* Phones lead with the editor; the row below names the selected
             step and opens the rail in a sheet — the same list, selection,
             and drag-reorder as the md+ sidebar. */}
         <div className="px-4 md:hidden">
            <button
               type="button"
               onClick={() => setRailOpen(true)}
               aria-label={_js('Choose a step to edit')}
               className="flex w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
               {selected === 'new' ? (
                  <>
                     <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-500">
                        <FaIcon name="plus" className="size-3.5" aria-hidden />
                     </span>
                     <span className="truncate font-medium">{_js('New step')}</span>
                  </>
               ) : selectedStep ? (
                  <>
                     {stepThumb(selectedStep) ? (
                        <img
                           src={stepThumb(selectedStep) ?? undefined}
                           alt=""
                           className="h-7 w-9 shrink-0 rounded border border-solid border-gray-300 object-cover"
                        />
                     ) : (
                        <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
                           <FaIcon name="image" className="size-3.5" aria-hidden />
                        </span>
                     )}
                     <span className="min-w-0 truncate">
                        <span className="font-medium">
                           {_js(
                              'Step %1',
                              String(
                                 guide.steps.findIndex(
                                    step => step.stepid === selectedStep.stepid
                                 ) + 1
                              )
                           )}
                        </span>
                        {selectedStep.title && <span>{_js(' — %1', selectedStep.title)}</span>}
                     </span>
                  </>
               ) : (
                  <span className="font-medium">{_js('Steps')}</span>
               )}
               <FaIcon
                  name="list-ul"
                  className="ml-auto size-4 shrink-0 text-gray-400"
                  aria-hidden
               />
            </button>
         </div>

         {/* The phone rail: the same list/footer as the sidebar, in a left
             sheet. Mounted only while open — rendering both copies doubled
             the work of every rail update. */}
         <Sheet open={railOpen} onOpenChange={setRailOpen}>
            <SheetContent side="left" className="w-80 max-w-[85vw] gap-0 p-4 md:hidden">
               <div className="mb-3 flex items-center justify-between">
                  <SheetTitle className="m-0 text-sm font-medium text-gray-950">
                     {_js('Steps')}
                  </SheetTitle>
                  <SheetClose
                     aria-label={_js('Close')}
                     className="flex size-7 items-center justify-center rounded text-gray-400 outline-none hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                     <FaIcon name="xmark" className="size-4" aria-hidden />
                  </SheetClose>
               </div>
               {railOpen && (
                  <>
                     <div className="-mx-1 min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 py-0.5 scrollbar-minimal">
                        <RailList
                           drag={drag}
                           entries={railEntries}
                           selected={selected}
                           hasVideo={video !== null}
                           onSelect={stepid => {
                              selectStep(stepid);
                              setRailOpen(false);
                           }}
                           onInsert={insertStepRelative}
                           onDuplicate={duplicateStep}
                        />
                     </div>
                     {/* Same divider as the desktop rail card's footer; the
                         negative margins span it across the sheet's padding. */}
                     <div className="-mx-4 border-t border-gray-200 px-4 pt-3">
                        <RailFooter
                           addDisabled={selected === 'new'}
                           orderDirty={orderDirty}
                           orderSaving={stepOrder.isPending}
                           onAdd={() => {
                              stageNewStep(null, null);
                              setRailOpen(false);
                           }}
                           onSaveOrder={saveOrder}
                        />
                     </div>
                  </>
               )}
            </SheetContent>
         </Sheet>

         {/* h-0 + min-h-full: the card contributes nothing to the grid
             row's height — the rail alone sets it — then stretches to
             exactly that height, scrolling its own content. The step's
             sticky action bar pins to the card's visible bottom. */}
         <SectionCard className="scrollbar-minimal md:h-0 md:min-h-full md:overflow-y-auto md:!pb-0">
            {selected === 'new' ? (
               <MemoStepEditor
                  // The nonce remounts the editor when a duplicate restages
                  // it while 'new' is already selected.
                  key={newStepSeed ? `new-step-${newStepSeed.nonce}` : 'new-step'}
                  guideid={guideid}
                  step={null}
                  seed={newStepSeed?.seed}
                  orderby={(draftIndex ?? guide.steps.length) + 1}
                  video={video}
                  onDirtyChange={setStepDirty}
                  onCreated={handleCreated}
                  onDeleted={handleDeleted}
               />
            ) : selectedStep ? (
               <MemoStepEditor
                  // Keyed by stepid only: the editor owns its post-save
                  // reseed, and keying on revisionid remounted it whenever
                  // a refetch landed, discarding anything typed in that
                  // window. External edits surface via the 409 conflict
                  // flow instead.
                  key={selectedStep.stepid}
                  guideid={guideid}
                  step={selectedStep}
                  seed={createdSeed?.stepid === selectedStep.stepid ? createdSeed.seed : undefined}
                  orderby={selectedStep.orderby}
                  video={video}
                  onDirtyChange={setStepDirty}
                  onCreated={noopCreated}
                  onDeleted={handleDeleted}
               />
            ) : (
               <p className="text-sm text-gray-500">
                  {_js('This guide has no steps yet. Add the first one.')}
               </p>
            )}
         </SectionCard>

         <AlertDialog
            open={pendingSelection !== null}
            onOpenChange={open => {
               if (open) {
                  return;
               }
               if (discardingRef.current) {
                  // discardAndSelect already applied the switch.
                  discardingRef.current = false;
                  return;
               }
               setPendingSelection(null);
               // A rejected switch abandons its staged new-step intent, so
               // the next plain "Add a step" doesn't replay it.
               stagedNewRef.current = { orderby: null, seed: null };
               // "Keep editing" after a back/forward: the URL already moved
               // to the rejected step; point it back at the one we kept.
               writeSelectionHash(selectedRef.current, { replace: true });
            }}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>{_js('Discard unsaved changes?')}</AlertDialogTitle>
                  <AlertDialogDescription>
                     {_js('This step has unsaved changes. Switching steps discards them.')}
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>{_js('Keep editing')}</AlertDialogCancel>
                  <AlertDialogAction onClick={discardAndSelect}>
                     {_js('Discard changes')}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
});

/**
 * The rail row for the step being composed in the new-step editor. It
 * previews where the step will land: it wears the insert position's
 * number, and dragging it moves the insert position rather than staging a
 * reorder. Dashed border because nothing exists server-side yet; no
 * onSelect because it's only rendered while it IS the selection.
 */
const DraftRailRow = memo(function DraftRailRow({
   displayIndex,
   isDragging,
   rowProps,
   handleProps,
}: {
   displayIndex: number;
   isDragging: boolean;
   rowProps: Drag['rowProps'];
   handleProps: Drag['handleProps'];
}) {
   return (
      <div
         data-railstep="new"
         {...rowProps(displayIndex, { draggable: true })}
         className={cn(
            'group flex cursor-grab items-center gap-2 rounded-lg border border-dashed border-brand-500 bg-white p-2 ring-1 ring-brand-500 active:cursor-grabbing',
            isDragging && 'opacity-60 ring-2 ring-brand-300'
         )}
      >
         <span
            {...handleProps(displayIndex, {
               label: _js('Reorder the new step (use the arrow keys to move it)'),
            })}
            className="relative flex size-6 shrink-0 cursor-grab items-center justify-center rounded text-gray-300 outline-none group-hover:text-gray-500 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
         >
            <FaIcon name="grip-vertical" className="size-3.5" aria-hidden />
         </span>
         <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
            <span className="flex h-9 w-12 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
               <FaIcon name="plus" className="size-4" />
            </span>
            <span className="min-w-0">
               <span className="block text-xs font-medium text-gray-500">
                  {_js('Step %1', String(displayIndex + 1))}
               </span>
               <span className="block truncate text-sm text-gray-500 italic">
                  {_js('New step')}
               </span>
            </span>
         </span>
      </div>
   );
});

/**
 * One rail entry. memo'd because the rail can hold 100+ steps and renders
 * on every drag preview update and selection change; the hook's stable
 * rowProps/handleProps let a drag re-render only the rows that moved.
 * Reorder by dragging the grip, or by focusing it and pressing the arrow
 * keys (Home/End jump to an end); there are no separate move buttons.
 */
/**
 * The step rail's list: the same markup in the md+ sidebar column and in the
 * phone sheet — only the select handler differs (the sheet's closes it).
 */
function RailList({
   drag,
   entries,
   selected,
   hasVideo,
   onSelect,
   onInsert,
   onDuplicate,
}: {
   drag: Drag;
   entries: (ApiStep | 'draft')[];
   selected: Selection;
   hasVideo: boolean;
   onSelect: (stepid: number) => void;
   onInsert: (stepid: number, where: 'above' | 'below') => void;
   onDuplicate: (stepid: number) => void;
}) {
   return (
      <div className="flex flex-col gap-2">
         <span aria-live="polite" className="sr-only">
            {drag.announcement}
         </span>
         {drag
            .displayItems(entries)
            .map(({ item }, displayIndex) =>
               item === 'draft' ? (
                  <DraftRailRow
                     key="draft"
                     displayIndex={displayIndex}
                     isDragging={drag.draggingIndex === displayIndex}
                     rowProps={drag.rowProps}
                     handleProps={drag.handleProps}
                  />
               ) : (
                  <RailRow
                     key={item.stepid}
                     step={item}
                     displayIndex={displayIndex}
                     isSelected={item.stepid === selected}
                     isDragging={drag.draggingIndex === displayIndex}
                     hasVideo={hasVideo}
                     rowProps={drag.rowProps}
                     handleProps={drag.handleProps}
                     onSelect={onSelect}
                     onInsert={onInsert}
                     onDuplicate={onDuplicate}
                  />
               )
            )}
      </div>
   );
}

/**
 * Add a step keeps the full row until an unsaved order needs a home; then
 * the two share it, or stack full-width when they cannot both fit — the
 * labels are `whitespace-nowrap`, so a long translation would otherwise
 * push the pair past the rail. Kept outside the rail's scroll area so a
 * long guide never buries the buttons.
 */
function RailFooter({
   addDisabled,
   orderDirty,
   orderSaving,
   onAdd,
   onSaveOrder,
}: {
   addDisabled: boolean;
   orderDirty: boolean;
   orderSaving: boolean;
   onAdd: () => void;
   onSaveOrder: () => void;
}) {
   return (
      <div className="flex flex-wrap gap-2">
         <Button
            type="button"
            className={cn('button-md button-outline', 'flex-auto justify-center')}
            // Manual adds append; only URL and rail-menu intents insert.
            onClick={onAdd}
            disabled={addDisabled}
         >
            <FaIcon name="plus" className="mr-2 size-3.5" />
            {_js('Add a step')}
         </Button>
         {orderDirty && (
            <PrimaryButton
               className="flex-auto justify-center"
               isPending={orderSaving}
               onClick={onSaveOrder}
            >
               {orderSaving ? _js('Saving...') : _js('Save step order')}
            </PrimaryButton>
         )}
      </div>
   );
}

const RailRow = memo(function RailRow({
   step,
   displayIndex,
   isSelected,
   isDragging,
   hasVideo,
   rowProps,
   handleProps,
   onSelect,
   onInsert,
   onDuplicate,
}: {
   step: ApiStep;
   displayIndex: number;
   isSelected: boolean;
   isDragging: boolean;
   /** The guide has a video, so a step's visual is its video segment and a
       missing step image isn't a gap. */
   hasVideo: boolean;
   rowProps: Drag['rowProps'];
   handleProps: Drag['handleProps'];
   onSelect: (stepid: number) => void;
   /** Open the new-step editor inserting next to this step. */
   onInsert: (stepid: number, where: 'above' | 'below') => void;
   /** Open the new-step editor prefilled with this step's content. */
   onDuplicate: (stepid: number) => void;
}) {
   const thumb = stepThumb(step);
   // A publish-readiness nudge (not an error): a step with no image or a
   // blank line. Derived from the step prop (and the guide's hasVideo) so the
   // memoized row re-renders only when those change.
   const attention = [
      !hasVideo && step.media.type === 'image' && step.media.data.length === 0
         ? _js('No image yet')
         : null,
      step.lines.some(line => line.text_raw.trim().length === 0) ? _js('Has an empty line') : null,
   ]
      .filter(reason => reason !== null)
      .reduce((list, reason) => (list === '' ? reason : _js('%1, %2', list, reason)), '');
   return (
      <div
         data-railstep={step.stepid}
         {...rowProps(displayIndex, { draggable: true })}
         className={cn(
            // Grab cursor across the whole row: a press anywhere reorders it
            // (the ⋮ menu overrides back to a pointer).
            'group flex cursor-grab items-center gap-2 rounded-lg border bg-white p-2 active:cursor-grabbing',
            isSelected ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-gray-400',
            isDragging && 'opacity-60 ring-2 ring-brand-300'
         )}
      >
         {/* Dragging works anywhere on the row (see rowProps draggable); the
             grip stays as the touch and keyboard affordance — its
             touch-action:none is what lets a touch drag from it, while a touch
             on the row body still scrolls the rail. Focus it and use the arrow
             keys to reorder without a pointer. */}
         <span
            {...handleProps(displayIndex, {
               label: _js(
                  'Reorder Step %1 (use the arrow keys to move it)',
                  String(displayIndex + 1)
               ),
            })}
            className="relative flex size-6 shrink-0 cursor-grab items-center justify-center rounded text-gray-300 outline-none group-hover:text-gray-500 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
         >
            <FaIcon name="grip-vertical" className="size-3.5" aria-hidden />
         </span>
         {/* data-drag: this button is the row's body, so a press that turns
             into a drag reorders (a plain click still selects the step). */}
         <button
            type="button"
            data-drag
            className="flex min-w-0 flex-1 cursor-grab items-center gap-2 text-left active:cursor-grabbing"
            onClick={() => onSelect(step.stepid)}
         >
            {thumb ? (
               <img
                  src={thumb}
                  alt=""
                  className="h-9 w-12 shrink-0 rounded border border-solid border-gray-300 object-cover"
               />
            ) : (
               <div className="flex h-9 w-12 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
                  <FaIcon name="image" className="size-4" />
               </div>
            )}
            <span className="min-w-0">
               <span className="block text-xs font-medium text-gray-500">
                  {_js('Step %1', String(displayIndex + 1))}
               </span>
               {step.title && (
                  <span className="block truncate text-sm text-gray-900">{step.title}</span>
               )}
            </span>
         </button>
         {/* Reserved slot, always rendered, so a step flipping ready ⇄
             unready never resizes the row or shifts its neighbors. */}
         <span className="flex size-4 shrink-0 items-center justify-center">
            {attention !== '' && (
               <Tooltip>
                  <TooltipTrigger asChild>
                     <span className="size-2 rounded-full bg-amber-500/70">
                        <span className="sr-only">{attention}</span>
                     </span>
                  </TooltipTrigger>
                  <TooltipContent>{attention}</TooltipContent>
               </Tooltip>
            )}
         </span>
         {/* Per-row actions: quiet until the row is hovered or focused, but
             always visible on touch, where there is no hover to reveal it. */}
         <DropdownMenu>
            <DropdownMenuTrigger
               aria-label={_js('Step %1 actions', String(displayIndex + 1))}
               className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-gray-400 opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50 data-[state=open]:opacity-100 pointer-coarse:opacity-100"
            >
               <FaIcon name="ellipsis-vertical" className="size-3.5" />
            </DropdownMenuTrigger>
            {/* Above the phone sheet (z-150): the menu portals to <body>,
                so its default z-50 would open behind the sheet panel. */}
            <DropdownMenuContent align="end" className="z-[200]">
               <DropdownMenuItem onSelect={() => onInsert(step.stepid, 'above')}>
                  {_js('Insert step above')}
               </DropdownMenuItem>
               <DropdownMenuItem onSelect={() => onInsert(step.stepid, 'below')}>
                  {_js('Insert step below')}
               </DropdownMenuItem>
               <DropdownMenuItem onSelect={() => onDuplicate(step.stepid)}>
                  {_js('Duplicate step')}
               </DropdownMenuItem>
            </DropdownMenuContent>
         </DropdownMenu>
      </div>
   );
});
